/**
 * Error code the API returns (HTTP 503, body `code`) when the store's own system, such as the GTX web service,
 * did not answer. Distinct from a Raptor fault, so staff are told the store system is offline.
 */
export const UPSTREAM_UNAVAILABLE_CODE = 'UpstreamUnavailable';

/**
 * Response header that repeats {@link UPSTREAM_UNAVAILABLE_CODE}. Set on errors, and on 200 responses that carry
 * last-known data because the store's system did not answer (My Work), so shared HTTP handling can tell.
 */
export const UPSTREAM_UNAVAILABLE_HEADER = 'X-Raptor-Error-Code';

/**
 * Translation key for the store-offline message. Matches the key the API resolves, so a tenant or branch
 * rewording from Settings > Wording applies on both sides.
 */
export const UPSTREAM_UNAVAILABLE_KEY = {
    feature: 'Common',
    component: 'Errors',
    label: 'The workshop system did not respond. Please try again shortly.',
} as const;

/**
 * Returns the store-offline message for an API error carrying {@link UPSTREAM_UNAVAILABLE_CODE}, or null for
 * any other error. The message comes from the given dictionary so tenant wording applies even on the login
 * screen, where the API cannot know the tenant yet: the device keeps the dictionary from its last session.
 * Falls back to the English label when the device has no dictionary or no entry for the key.
 *
 * @param error - The failed response, typically an HttpErrorResponse whose `error` holds the JSON body.
 * @param dictionary - The cached translation dictionary keyed `feature|component|label`, or null.
 */
export function upstreamUnavailableMessage(error: unknown, dictionary: Map<string, string> | null): string | null {
    const code = (error as { error?: { code?: unknown } } | null)?.error?.code;
    if (code !== UPSTREAM_UNAVAILABLE_CODE) {
        return null;
    }

    const key = `${UPSTREAM_UNAVAILABLE_KEY.feature}|${UPSTREAM_UNAVAILABLE_KEY.component}|${UPSTREAM_UNAVAILABLE_KEY.label}`;
    return dictionary?.get(key) || UPSTREAM_UNAVAILABLE_KEY.label;
}
